/**
 * Dispute Management - Redesigned with EntityManagement design system
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  CircularProgress,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import BlockIcon from '@mui/icons-material/Block';
import WarningIcon from '@mui/icons-material/Warning';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import InboxIcon from '@mui/icons-material/Inbox';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import UnifiedResolutionDialog, {
  DisputeData,
  ActionType,
  ActionPayload,
} from '../components/arbitration/modals/UnifiedResolutionDialog';

import './EntityManagement.css';

interface DisputeInfo {
  reason: string;
  description: string;
  status: 'pending_admin_approval' | 'approved' | 'rejected' | 'investigating' | 'resolved' | 'closed';
  openedBy: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  openedAt: string;
  adminApprovedBy?: string;
  adminApprovedAt?: string;
  adminRejectedBy?: string;
  adminRejectedAt?: string;
  adminRejectReason?: string;
  chatId?: string;
  resolvedBy?: string;
  resolvedAt?: string;
  resolution?: string;
  resolutionType?: 'refund' | 'reject' | 'compromise' | 'force_deliver' | 'redelivery';
  refundAmount?: number;
  refundedAt?: string;
  penalties?: Array<{ userId: string; userName?: string; points: number }>;
  restrictions?: Array<{ userId: string; userName?: string; restrict: boolean }>;
  tags?: string[];
  actionHistory?: Array<{
    action: string;
    performedBy?: string;
    performedByName?: string;
    performedAt?: string;
    details?: any;
  }>;
}

interface OrderDispute {
  _id: string;
  orderNumber: string;
  userId: { _id: string; firstName: string; lastName: string; email: string };
  disputeInfo: DisputeInfo;
  totalAmount: number;
  createdAt: string;
}

interface OrderItemDispute {
  _id: string;
  productTitle: string;
  quantity: number;
  totalPrice: number;
  orderId: {
    _id: string;
    orderNumber: string;
    userId: { _id: string; firstName: string; lastName: string; email: string };
  };
  storeId: { _id: string; storeName: string };
  disputeInfo: DisputeInfo;
  createdAt: string;
  fulfillmentType?: 'account_delivery' | 'info_only' | 'postal_delivery';
  postalDeliveryDetails?: {
    shippingCost?: number;
    trackingNumber?: string;
    shippingAddress?: any;
    shippingStatus?: string;
  };
}

interface DisputeStats {
  orderDisputes: { total: number; pending: number; approved: number; rejected: number };
  orderItemDisputes: { total: number; pending: number; approved: number; rejected: number };
  overall: { total: number; pending: number; approved: number; rejected: number };
}

type DisputeKind = 'order' | 'item';

const statusLabels: Record<string, string> = {
  pending_admin_approval: 'در انتظار تأیید ادمین',
  approved: 'تأیید شده',
  rejected: 'رد شده',
  investigating: 'در حال بررسی',
  resolved: 'حل شده',
  closed: 'بسته شده',
};

const statusTagClass: Record<string, string> = {
  pending_admin_approval: 'tag tag-sm tag-warning',
  approved: 'tag tag-sm tag-success',
  rejected: 'tag tag-sm tag-danger',
  investigating: 'tag tag-sm tag-active',
  resolved: 'tag tag-sm tag-success',
  closed: 'tag tag-sm tag-inactive',
};

const kindLabels: Record<DisputeKind, string> = {
  order: 'سفارش',
  item: 'محصول',
};

const DisputeManagement: React.FC = () => {
  const navigate = useNavigate();

  const [orderDisputes, setOrderDisputes] = useState<OrderDispute[]>([]);
  const [orderItemDisputes, setOrderItemDisputes] = useState<OrderItemDispute[]>([]);
  const [disputeStats, setDisputeStats] = useState<DisputeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Filters / paging
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterKind, setFilterKind] = useState<DisputeKind | ''>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialogs
  const [selectedDispute, setSelectedDispute] = useState<OrderDispute | OrderItemDispute | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [resolveReason, setResolveReason] = useState('');
  const [disputeType, setDisputeType] = useState<'order' | 'item'>('order');

  // Refund
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [includeShipping, setIncludeShipping] = useState(true);

  // Unified dialog
  const [unifiedDialogOpen, setUnifiedDialogOpen] = useState(false);
  const [unifiedDialogError, setUnifiedDialogError] = useState<string | null>(null);
  const [selectedUnifiedDispute, setSelectedUnifiedDispute] = useState<DisputeData | null>(null);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    await Promise.all([fetchPendingDisputes(), fetchDisputeStats()]);
  };

  const fetchPendingDisputes = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/disputes/pending');
      setOrderDisputes(response.data.data.orderDisputes);
      setOrderItemDisputes(response.data.data.orderItemDisputes);
    } catch (error) {
      console.error('Error fetching pending disputes:', error);
      showSnackbar('خطا در بارگذاری اختلافات', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchDisputeStats = async () => {
    try {
      const response = await apiClient.get('/admin/disputes/stats');
      setDisputeStats(response.data.data);
    } catch (error) {
      console.error('Error fetching dispute stats:', error);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleApproveDispute = async (dispute: OrderDispute | OrderItemDispute, type: 'order' | 'item') => {
    try {
      setActionLoading(dispute._id);
      const endpoint = type === 'order'
        ? `/admin/disputes/orders/${dispute._id}/approve`
        : `/admin/disputes/items/${dispute._id}/approve`;
      const response = await apiClient.post(endpoint);
      if (response.data.status === 'success') {
        showSnackbar('اختلاف با موفقیت تأیید شد و چت گروهی ایجاد شد', 'success');
        fetchPendingDisputes();
        fetchDisputeStats();
      }
    } catch (error) {
      console.error('Error approving dispute:', error);
      showSnackbar('خطا در تأیید اختلاف', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectDispute = async () => {
    if (!selectedDispute || !rejectReason.trim()) {
      showSnackbar('لطفا دلیل رد را وارد کنید', 'warning');
      return;
    }
    try {
      setActionLoading(selectedDispute._id);
      const endpoint = disputeType === 'order'
        ? `/admin/disputes/orders/${selectedDispute._id}/reject`
        : `/admin/disputes/items/${selectedDispute._id}/reject`;
      const response = await apiClient.post(endpoint, { reason: rejectReason });
      if (response.data.status === 'success') {
        showSnackbar('اختلاف با موفقیت رد شد', 'success');
        fetchPendingDisputes();
        fetchDisputeStats();
        setShowRejectDialog(false);
        setRejectReason('');
        setSelectedDispute(null);
      }
    } catch (error) {
      console.error('Error rejecting dispute:', error);
      showSnackbar('خطا در رد اختلاف', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResolveDispute = async () => {
    if (!selectedDispute || !resolveReason.trim()) {
      showSnackbar('لطفا نتیجه حل اختلاف را وارد کنید', 'warning');
      return;
    }
    try {
      setActionLoading(selectedDispute._id);
      const endpoint = disputeType === 'order'
        ? `/admin/disputes/orders/${selectedDispute._id}/resolve`
        : `/admin/disputes/items/${selectedDispute._id}/resolve`;
      const response = await apiClient.post(endpoint, { resolution: resolveReason });
      if (response.data.status === 'success') {
        showSnackbar('اختلاف با موفقیت حل شد', 'success');
        fetchPendingDisputes();
        fetchDisputeStats();
        setShowResolveDialog(false);
        setResolveReason('');
        setSelectedDispute(null);
      }
    } catch (error) {
      console.error('Error resolving dispute:', error);
      showSnackbar('خطا در حل اختلاف', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRefundItem = async () => {
    if (!selectedDispute || disputeType !== 'item') return;
    try {
      setActionLoading(selectedDispute._id);
      const response = await apiClient.post(`/admin/orders/items/${selectedDispute._id}/refund`, {
        reason: refundReason || 'بازپرداخت بابت حل اختلاف',
        includeShipping,
      });
      if (response.data.status === 'success') {
        const refundDetails = response.data.data.refundDetails;
        showSnackbar(
          `بازپرداخت موفق: ${formatPrice(refundDetails.refundedAmount)} تومان به کیف پول کاربر واریز شد`,
          'success'
        );
        fetchPendingDisputes();
        fetchDisputeStats();
        setShowRefundDialog(false);
        setSelectedDispute(null);
      }
    } catch (error: any) {
      console.error('Error refunding item:', error);
      showSnackbar(error.response?.data?.message || 'خطا در بازپرداخت', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const openUnifiedDialogForOrder = (dispute: OrderDispute) => {
    const disputeData: DisputeData = {
      id: dispute._id,
      disputeNumber: dispute.orderNumber,
      type: 'order',
      status: dispute.disputeInfo.status as any,
      reason: dispute.disputeInfo.reason,
      reasonLabel: dispute.disputeInfo.reason,
      description: dispute.disputeInfo.description,
      parties: {
        initiator: {
          id: dispute.userId._id,
          name: `${dispute.userId.firstName} ${dispute.userId.lastName}`,
          email: dispute.userId.email,
          role: 'buyer',
        },
      },
      financial: { amount: dispute.totalAmount, refundableAmount: dispute.totalAmount },
      references: { orderNumber: dispute.orderNumber, orderId: dispute._id },
    };
    setSelectedUnifiedDispute(disputeData);
    setUnifiedDialogError(null);
    setUnifiedDialogOpen(true);
  };

  const openUnifiedDialogForItem = (dispute: OrderItemDispute) => {
    const shippingCost = dispute.fulfillmentType === 'postal_delivery'
      ? (dispute.postalDeliveryDetails?.shippingCost || 0) * (dispute.quantity || 1)
      : 0;
    const disputeData: DisputeData = {
      id: dispute._id,
      disputeNumber: dispute.orderId?.orderNumber || dispute._id,
      type: 'order_item',
      status: dispute.disputeInfo?.status as any,
      reason: dispute.disputeInfo?.reason || '',
      reasonLabel: dispute.disputeInfo?.reason || '',
      description: dispute.disputeInfo?.description || '',
      parties: {
        initiator: {
          id: dispute.orderId?.userId?._id || '',
          name: `${dispute.orderId?.userId?.firstName || ''} ${dispute.orderId?.userId?.lastName || ''}`,
          email: dispute.orderId?.userId?.email,
          role: 'buyer',
        },
        respondent: {
          id: dispute.storeId?._id || '',
          name: dispute.storeId?.storeName || 'فروشگاه نامشخص',
          role: 'seller',
        },
      },
      financial: {
        amount: dispute.totalPrice,
        shippingCost,
        refundableAmount: dispute.totalPrice + shippingCost,
      },
      references: {
        orderNumber: dispute.orderId?.orderNumber,
        orderId: dispute.orderId?._id,
        orderItemId: dispute._id,
        productTitle: dispute.productTitle,
        storeName: dispute.storeId?.storeName,
      },
      currentItemStatus: 'disputed',
    };
    setSelectedUnifiedDispute(disputeData);
    setUnifiedDialogError(null);
    setUnifiedDialogOpen(true);
  };

  const handleUnifiedAction = async (actionType: ActionType, payload: ActionPayload) => {
    if (!selectedUnifiedDispute) return;
    const disputeId = selectedUnifiedDispute.id;
    const isOrderItem = selectedUnifiedDispute.type === 'order_item';
    setActionLoading(disputeId);
    setUnifiedDialogError(null);
    try {
      let response;
      const baseEndpoint = isOrderItem ? '/admin/disputes/items' : '/admin/disputes/orders';
      switch (actionType) {
        case 'approve':
          response = await apiClient.post(`${baseEndpoint}/${disputeId}/approve`);
          break;
        case 'reject':
          response = await apiClient.post(`${baseEndpoint}/${disputeId}/reject`, { reason: payload.reason });
          break;
        case 'refund':
          if (isOrderItem) {
            response = await apiClient.post(`/admin/unified-disputes/${disputeId}/refund`, {
              amount: payload.refundAmount,
              reason: payload.reason || 'بازپرداخت بابت حل اختلاف',
              applyPenalty: payload.applyPenalty,
              includeShipping: payload.includeShipping,
            });
          }
          break;
        case 'resolve_buyer':
        case 'resolve_seller':
        case 'resolve_mutual':
          response = await apiClient.post(`${baseEndpoint}/${disputeId}/resolve`, { resolution: payload.resolution });
          break;
        case 'change_status':
          response = await apiClient.post(`/admin/unified-disputes/${disputeId}/change-status`, {
            newStatus: payload.newStatus,
            reason: payload.reason,
          });
          break;
        case 'cancel_order':
          // ✅ FIX BUG-7: "cancel order" on a PAID dispute must actually refund the buyer.
          // The old call hit change-status {cancelled}, which moved NO money (and the backend
          // now rejects that transition). Route it through the real refund endpoint.
          if (isOrderItem) {
            response = await apiClient.post(`/admin/unified-disputes/${disputeId}/refund`, {
              amount: payload.refundAmount,
              reason: payload.reason || 'لغو و بازپرداخت بابت حل اختلاف',
              applyPenalty: payload.applyPenalty,
              includeShipping: payload.includeShipping,
            });
          } else {
            response = await apiClient.post(`${baseEndpoint}/${disputeId}/refund`, {
              reason: payload.reason || 'لغو و بازپرداخت بابت حل اختلاف',
            });
          }
          break;
        default:
          throw new Error('عملیات نامشخص');
      }
      if (response?.data?.status === 'success') {
        const messages: Record<ActionType, string> = {
          approve: 'اختلاف با موفقیت تأیید شد',
          reject: 'اختلاف با موفقیت رد شد',
          refund: response.data.message || 'بازپرداخت با موفقیت انجام شد',
          resolve_buyer: 'اختلاف به نفع خریدار حل شد',
          resolve_seller: 'اختلاف به نفع فروشنده حل شد',
          resolve_mutual: 'اختلاف با توافق طرفین حل شد',
          change_status: 'وضعیت با موفقیت تغییر کرد',
          cancel_order: response.data.message || 'سفارش لغو و مبلغ به خریدار بازپرداخت شد',
        };
        showSnackbar(messages[actionType], 'success');
        setUnifiedDialogOpen(false);
        fetchPendingDisputes();
        fetchDisputeStats();
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'خطا در انجام عملیات';
      setUnifiedDialogError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const formatPrice = (price: number) => new Intl.NumberFormat('fa-IR').format(price);

  // Build unified rows
  type Row = {
    id: string;
    kind: DisputeKind;
    title: string;
    subtitle: string;
    status: string;
    buyerName: string;
    buyerEmail: string;
    amount?: number;
    createdAt: string;
    chatId?: string;
    raw: any;
  };

  const buildRows = (): Row[] => {
    const rows: Row[] = [];
    orderDisputes.forEach((d) => {
      rows.push({
        id: d._id,
        kind: 'order',
        title: `سفارش #${d.orderNumber}`,
        subtitle: `مبلغ: ${formatPrice(d.totalAmount)} تومان`,
        status: d.disputeInfo.status,
        buyerName: `${d.userId.firstName} ${d.userId.lastName}`,
        buyerEmail: d.userId.email,
        amount: d.totalAmount,
        createdAt: d.createdAt,
        chatId: d.disputeInfo.chatId,
        raw: d,
      });
    });
    orderItemDisputes.forEach((d) => {
      if (!d.orderId) {
        rows.push({
          id: d._id,
          kind: 'item',
          title: d.productTitle || 'نامشخص',
          subtitle: 'سفارش حذف شده',
          status: d.disputeInfo?.status || 'closed',
          buyerName: '—',
          buyerEmail: '',
          amount: d.totalPrice,
          createdAt: d.createdAt,
          chatId: d.disputeInfo?.chatId,
          raw: d,
        });
        return;
      }
      rows.push({
        id: d._id,
        kind: 'item',
        title: d.productTitle,
        subtitle: `${d.storeId?.storeName || '—'} · #${d.orderId.orderNumber}`,
        status: d.disputeInfo?.status,
        buyerName: `${d.orderId.userId?.firstName || ''} ${d.orderId.userId?.lastName || ''}`.trim() || '—',
        buyerEmail: d.orderId.userId?.email || '',
        amount: d.totalPrice,
        createdAt: d.createdAt,
        chatId: d.disputeInfo?.chatId,
        raw: d,
      });
    });
    return rows;
  };

  const allRows = buildRows();
  const filteredRows = allRows.filter((r) => {
    if (filterStatus && r.status !== filterStatus) return false;
    if (filterKind && r.kind !== filterKind) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      if (
        !r.title.toLowerCase().includes(q) &&
        !r.subtitle.toLowerCase().includes(q) &&
        !r.buyerName.toLowerCase().includes(q) &&
        !r.buyerEmail.toLowerCase().includes(q)
      ) {
        return false;
      }
    }
    return true;
  });

  const totalItems = filteredRows.length;
  const totalPages = Math.ceil(totalItems / rowsPerPage);
  const startItem = totalItems === 0 ? 0 : page * rowsPerPage + 1;
  const endItem = Math.min((page + 1) * rowsPerPage, totalItems);
  const pagedRows = filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Stats
  const total = disputeStats?.overall.total ?? allRows.length;
  const pendingCount = disputeStats?.overall.pending ?? allRows.filter((r) => r.status === 'pending_admin_approval').length;
  const approvedCount = disputeStats?.overall.approved ?? allRows.filter((r) => r.status === 'approved' || r.status === 'resolved').length;
  const rejectedCount = disputeStats?.overall.rejected ?? allRows.filter((r) => r.status === 'rejected').length;

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPage(0);
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };
  const clearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setFilterStatus('');
    setFilterKind('');
    setPage(0);
  };

  const hasActiveFilters = !!searchTerm || !!filterStatus || !!filterKind;

  const openDetails = (row: Row) => {
    setSelectedDispute(row.raw);
    setDisputeType(row.kind === 'order' ? 'order' : 'item');
    setShowDetailsDialog(true);
  };
  const openApproveQuick = (row: Row) => {
    if (row.kind === 'order' || row.kind === 'item') {
      handleApproveDispute(row.raw, row.kind);
    }
  };
  const openRejectDialog = (row: Row) => {
    if (row.kind === 'order' || row.kind === 'item') {
      setSelectedDispute(row.raw);
      setDisputeType(row.kind);
      setShowRejectDialog(true);
    }
  };
  const openResolveDialog = (row: Row) => {
    if (row.kind === 'order' || row.kind === 'item') {
      setSelectedDispute(row.raw);
      setDisputeType(row.kind);
      setShowResolveDialog(true);
    }
  };
  const openRefundDialog = (row: Row) => {
    if (row.kind === 'item') {
      setSelectedDispute(row.raw);
      setDisputeType('item');
      setRefundReason('');
      setIncludeShipping(true);
      setShowRefundDialog(true);
    }
  };
  const openUnified = (row: Row) => {
    if (row.kind === 'order') openUnifiedDialogForOrder(row.raw);
    else if (row.kind === 'item') openUnifiedDialogForItem(row.raw);
  };

  const kindIcon = (kind: DisputeKind) => {
    if (kind === 'order') return <ShoppingCartOutlinedIcon fontSize="small" />;
    return <StorefrontOutlinedIcon fontSize="small" />;
  };

  return (
    <div className="entity-page">
      {/* Header */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">مدیریت اعتراضات</h1>
          <p className="entity-subtitle">بررسی و رسیدگی به اعتراضات سفارشات</p>
        </div>
        <div className="header-actions">
          <button className="header-btn refresh" onClick={fetchAll} title="بروزرسانی">
            <RefreshIcon fontSize="small" />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="entity-stats">
        <div className="entity-stat-card">
          <div className="entity-stat-icon blue"><GavelIcon /></div>
          <div>
            <p className="entity-stat-label">کل اعتراضات</p>
            <p className="entity-stat-value">{total}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon orange"><HourglassEmptyIcon /></div>
          <div>
            <p className="entity-stat-label">در انتظار</p>
            <p className="entity-stat-value">{pendingCount}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon green"><CheckCircleIcon /></div>
          <div>
            <p className="entity-stat-label">حل/تایید شده</p>
            <p className="entity-stat-value">{approvedCount}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon red"><BlockIcon /></div>
          <div>
            <p className="entity-stat-label">رد شده</p>
            <p className="entity-stat-value">{rejectedCount}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="جستجو بر اساس سفارش، محصول، کاربر..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">نوع</label>
            <select
              className="filter-field"
              value={filterKind}
              onChange={(e) => { setFilterKind(e.target.value as DisputeKind | ''); setPage(0); }}
            >
              <option value="">همه انواع</option>
              <option value="order">سفارش</option>
              <option value="item">محصول</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">وضعیت</label>
            <select
              className="filter-field"
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}
            >
              <option value="">همه وضعیت‌ها</option>
              <option value="pending_admin_approval">در انتظار تأیید</option>
              <option value="approved">تأیید شده</option>
              <option value="investigating">در حال بررسی</option>
              <option value="resolved">حل شده</option>
              <option value="rejected">رد شده</option>
              <option value="closed">بسته شده</option>
            </select>
          </div>

          <div className="filter-group filter-group-actions">
            <button className="filter-btn" onClick={handleSearch}>
              <SearchIcon fontSize="small" />
              اعمال
            </button>
            {hasActiveFilters && (
              <button className="filter-btn outline" onClick={clearFilters}>
                پاک
              </button>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <div className="filters-active">
            <span className="filters-label">فیلترهای فعال:</span>
            {searchTerm && (
              <span className="filter-tag">
                جستجو: {searchTerm}
                <button onClick={() => { setSearchTerm(''); setSearchInput(''); }}>×</button>
              </span>
            )}
            {filterKind && (
              <span className="filter-tag">
                نوع: {kindLabels[filterKind as DisputeKind]}
                <button onClick={() => setFilterKind('')}>×</button>
              </span>
            )}
            {filterStatus && (
              <span className="filter-tag">
                وضعیت: {statusLabels[filterStatus] || filterStatus}
                <button onClick={() => setFilterStatus('')}>×</button>
              </span>
            )}
            <button className="filters-clear" onClick={clearFilters}>پاک کردن همه</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="entity-table-wrapper">
        {loading && allRows.length === 0 ? (
          <div className="entity-loading"><CircularProgress size={32} /></div>
        ) : filteredRows.length === 0 ? (
          <div className="entity-empty">
            <div className="entity-empty-icon"><InboxIcon style={{ fontSize: 48 }} /></div>
            <p className="entity-empty-text">اعتراضی یافت نشد</p>
          </div>
        ) : (
          <>
            <table className="entity-table">
              <thead>
                <tr>
                  <th>نوع</th>
                  <th>عنوان</th>
                  <th>خریدار</th>
                  <th>مبلغ</th>
                  <th>وضعیت</th>
                  <th>تاریخ</th>
                  <th style={{ width: 200 }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {pagedRows.map((row) => (
                  <tr key={`${row.kind}-${row.id}`}>
                    <td>
                      <div className="entity-info-cell">
                        <div className="entity-image">{kindIcon(row.kind)}</div>
                        <div className="entity-info">
                          <span className="entity-name">{kindLabels[row.kind]}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="entity-info">
                        <span className="entity-name">{row.title}</span>
                        <span className="entity-desc">{row.subtitle}</span>
                      </div>
                    </td>
                    <td>
                      <div className="entity-info">
                        <span className="entity-name">{row.buyerName}</span>
                        {row.buyerEmail && <span className="entity-desc">{row.buyerEmail}</span>}
                      </div>
                    </td>
                    <td>
                      <span className="entity-desc">
                        {row.amount != null ? `${formatPrice(row.amount)} تومان` : '—'}
                      </span>
                    </td>
                    <td>
                      <span className={statusTagClass[row.status] || 'tag tag-sm tag-inactive'}>
                        {statusLabels[row.status] || row.status}
                      </span>
                    </td>
                    <td><span className="entity-desc">{formatDate(row.createdAt)}</span></td>
                    <td>
                      <div className="actions-cell">
                        {(
                          <>
                            <Tooltip title="مدیریت جامع" arrow>
                              <button className="action-btn edit" onClick={() => openUnified(row)} disabled={actionLoading === row.id}>
                                <GavelIcon />
                              </button>
                            </Tooltip>
                            <Tooltip title="جزئیات" arrow>
                              <button className="action-btn edit" onClick={() => openDetails(row)}>
                                <VisibilityIcon />
                              </button>
                            </Tooltip>
                            {row.status === 'pending_admin_approval' && (
                              <>
                                <Tooltip title="تأیید سریع" arrow>
                                  <button className="action-btn toggle active" onClick={() => openApproveQuick(row)} disabled={actionLoading === row.id}>
                                    {actionLoading === row.id ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                                  </button>
                                </Tooltip>
                                <Tooltip title="رد" arrow>
                                  <button className="action-btn delete" onClick={() => openRejectDialog(row)} disabled={actionLoading === row.id}>
                                    <CancelIcon />
                                  </button>
                                </Tooltip>
                              </>
                            )}
                            {row.status === 'approved' && (
                              <>
                                <Tooltip title="حل اختلاف" arrow>
                                  <button className="action-btn edit" onClick={() => openResolveDialog(row)} disabled={actionLoading === row.id}>
                                    <WarningIcon />
                                  </button>
                                </Tooltip>
                                {row.kind === 'item' && (
                                  <Tooltip title="بازپرداخت" arrow>
                                    <button className="action-btn edit" onClick={() => openRefundDialog(row)} disabled={actionLoading === row.id}>
                                      <MonetizationOnIcon />
                                    </button>
                                  </Tooltip>
                                )}
                              </>
                            )}
                            {row.chatId && (
                              <Tooltip title="چت گروهی" arrow>
                                <button className="action-btn edit" onClick={() => navigate('/chats', { state: { openChatId: row.chatId } })}>
                                  <ChatBubbleOutlineIcon />
                                </button>
                              </Tooltip>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="entity-cards">
              {pagedRows.map((row) => (
                <div key={`card-${row.kind}-${row.id}`} className="entity-card">
                  <div className="entity-card-header">
                    <div className="entity-card-image">{kindIcon(row.kind)}</div>
                    <div className="entity-card-info">
                      <div className="entity-card-name">{row.title}</div>
                      <div className="entity-card-desc">{row.subtitle}</div>
                    </div>
                    <div className="entity-card-status">
                      <span className={statusTagClass[row.status] || 'tag tag-sm tag-inactive'}>
                        {statusLabels[row.status] || row.status}
                      </span>
                    </div>
                  </div>
                  <div className="entity-card-actions">
                    {(
                      <>
                        <button className="entity-card-btn primary" onClick={() => openUnified(row)}>
                          <GavelIcon fontSize="small" /> مدیریت
                        </button>
                        <button className="entity-card-btn" onClick={() => openDetails(row)}>
                          <VisibilityIcon fontSize="small" /> جزئیات
                        </button>
                        {row.status === 'pending_admin_approval' && (
                          <>
                            <button className="entity-card-btn" onClick={() => openApproveQuick(row)}>
                              <CheckCircleIcon fontSize="small" /> تایید
                            </button>
                            <button className="entity-card-btn danger" onClick={() => openRejectDialog(row)}>
                              <CancelIcon fontSize="small" /> رد
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="entity-pagination">
              <div className="pagination-info">
                نمایش {startItem} تا {endItem} از {totalItems}
              </div>
              <div className="pagination-controls">
                <select
                  className="pagination-select"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <button className="pagination-btn" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>
                  بعدی
                </button>
                <span style={{ fontSize: 12 }}>{page + 1} / {totalPages || 1}</span>
                <button className="pagination-btn" onClick={() => setPage(page - 1)} disabled={page <= 0}>
                  قبلی
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Details Dialog */}
      <Dialog
        open={showDetailsDialog}
        onClose={() => setShowDetailsDialog(false)}
        maxWidth="md"
        fullWidth
        className="dispute-details-dialog"
      >
        <div className="edit-dialog-title">
          <span>جزئیات اعتراض</span>
          <button className="details-close" onClick={() => setShowDetailsDialog(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {selectedDispute && (
            <div className="edit-form">
              {/* Subject */}
              <div className="edit-card">
                <div className="edit-card-header">
                  اطلاعات {disputeType === 'order' ? 'سفارش' : 'محصول'}
                </div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    {disputeType === 'order' ? (
                      <>
                        <div className="edit-field">
                          <label>شماره سفارش</label>
                          <input type="text" readOnly value={(selectedDispute as OrderDispute).orderNumber} />
                        </div>
                        <div className="edit-field">
                          <label>مبلغ</label>
                          <input type="text" readOnly value={`${formatPrice((selectedDispute as OrderDispute).totalAmount)} تومان`} />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="edit-field">
                          <label>محصول</label>
                          <input type="text" readOnly value={(selectedDispute as OrderItemDispute).productTitle} />
                        </div>
                        <div className="edit-field">
                          <label>فروشگاه</label>
                          <input type="text" readOnly value={(selectedDispute as OrderItemDispute).storeId?.storeName || '—'} />
                        </div>
                        <div className="edit-field">
                          <label>شماره سفارش</label>
                          <input type="text" readOnly value={(selectedDispute as OrderItemDispute).orderId?.orderNumber || '—'} />
                        </div>
                        <div className="edit-field">
                          <label>تعداد</label>
                          <input type="text" readOnly value={String((selectedDispute as OrderItemDispute).quantity)} />
                        </div>
                        <div className="edit-field">
                          <label>مبلغ</label>
                          <input type="text" readOnly value={`${formatPrice((selectedDispute as OrderItemDispute).totalPrice)} تومان`} />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Buyer */}
              <div className="edit-card">
                <div className="edit-card-header">اطلاعات خریدار</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>نام</label>
                      <input
                        type="text"
                        readOnly
                        value={
                          disputeType === 'order'
                            ? `${(selectedDispute as OrderDispute).userId.firstName} ${(selectedDispute as OrderDispute).userId.lastName}`
                            : `${(selectedDispute as OrderItemDispute).orderId?.userId?.firstName || ''} ${(selectedDispute as OrderItemDispute).orderId?.userId?.lastName || ''}`.trim() || '—'
                        }
                      />
                    </div>
                    <div className="edit-field">
                      <label>ایمیل</label>
                      <input
                        type="text"
                        readOnly
                        value={
                          disputeType === 'order'
                            ? (selectedDispute as OrderDispute).userId.email
                            : (selectedDispute as OrderItemDispute).orderId?.userId?.email || '—'
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Dispute */}
              <div className="edit-card">
                <div className="edit-card-header">جزئیات اعتراض</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>وضعیت</label>
                      <div style={{ padding: '6px 0' }}>
                        <span className={statusTagClass[selectedDispute.disputeInfo.status] || 'tag tag-sm tag-inactive'}>
                          {statusLabels[selectedDispute.disputeInfo.status] || selectedDispute.disputeInfo.status}
                        </span>
                      </div>
                    </div>
                    <div className="edit-field">
                      <label>تاریخ ایجاد</label>
                      <input type="text" readOnly value={formatDate(selectedDispute.disputeInfo.openedAt)} />
                    </div>
                    <div className="edit-field full">
                      <label>دلیل</label>
                      <input type="text" readOnly value={selectedDispute.disputeInfo.reason || '—'} />
                    </div>
                    <div className="edit-field full">
                      <label>شرح</label>
                      <textarea readOnly value={selectedDispute.disputeInfo.description || '—'} rows={3} />
                    </div>
                    {selectedDispute.disputeInfo.adminApprovedAt && (
                      <div className="edit-field">
                        <label>تاریخ تأیید</label>
                        <input type="text" readOnly value={formatDate(selectedDispute.disputeInfo.adminApprovedAt)} />
                      </div>
                    )}
                    {selectedDispute.disputeInfo.adminRejectedAt && (
                      <div className="edit-field">
                        <label>تاریخ رد</label>
                        <input type="text" readOnly value={formatDate(selectedDispute.disputeInfo.adminRejectedAt)} />
                      </div>
                    )}
                    {selectedDispute.disputeInfo.adminRejectReason && (
                      <div className="edit-field full">
                        <label>دلیل رد</label>
                        <textarea readOnly value={selectedDispute.disputeInfo.adminRejectReason} rows={2} />
                      </div>
                    )}
                  </div>
                  {selectedDispute.disputeInfo.chatId && (
                    <div style={{ marginTop: 12 }}>
                      <button
                        className="edit-btn cancel"
                        onClick={() => navigate('/chats', { state: { openChatId: selectedDispute.disputeInfo.chatId } })}
                      >
                        <ChatBubbleOutlineIcon fontSize="small" /> مشاهده چت گروهی
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Resolution */}
              <div className="edit-card">
                <div className="edit-card-header">نتیجه و تصمیم‌گیری</div>
                <div className="edit-card-body">
                  <div className="edit-form-grid">
                    <div className="edit-field">
                      <label>نوع تصمیم</label>
                      <input
                        type="text"
                        readOnly
                        value={
                          selectedDispute.disputeInfo.resolutionType === 'refund' ? 'بازپرداخت'
                            : selectedDispute.disputeInfo.resolutionType === 'reject' ? 'رد اختلاف'
                              : selectedDispute.disputeInfo.resolutionType === 'compromise' ? 'توافق طرفین'
                                : selectedDispute.disputeInfo.resolutionType === 'force_deliver' ? 'تحویل اجباری'
                                  : selectedDispute.disputeInfo.resolutionType === 'redelivery' ? 'تحویل مجدد'
                                    : 'هنوز تصمیمی گرفته نشده'
                        }
                      />
                    </div>
                    <div className="edit-field">
                      <label>مبلغ بازپرداخت</label>
                      <input
                        type="text"
                        readOnly
                        value={
                          selectedDispute.disputeInfo.refundAmount && selectedDispute.disputeInfo.refundAmount > 0
                            ? `${formatPrice(selectedDispute.disputeInfo.refundAmount)} تومان`
                            : 'ندارد'
                        }
                      />
                    </div>
                    <div className="edit-field full">
                      <label>نتیجه</label>
                      <textarea readOnly value={selectedDispute.disputeInfo.resolution || 'ثبت نشده'} rows={2} />
                    </div>
                  </div>

                  {selectedDispute.disputeInfo.penalties && selectedDispute.disputeInfo.penalties.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>جریمه‌ها</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {selectedDispute.disputeInfo.penalties.map((p, i) => (
                          <span key={i} className="tag tag-sm tag-danger">
                            {p.userName || 'کاربر'}: -{p.points} امتیاز
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedDispute.disputeInfo.restrictions && selectedDispute.disputeInfo.restrictions.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>محدودیت‌ها</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {selectedDispute.disputeInfo.restrictions.map((r, i) => (
                          <span key={i} className={r.restrict ? 'tag tag-sm tag-danger' : 'tag tag-sm tag-success'}>
                            {r.userName || 'کاربر'}: {r.restrict ? 'محدود شده' : 'رفع محدودیت'}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedDispute.disputeInfo.tags && selectedDispute.disputeInfo.tags.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>برچسب‌ها</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {selectedDispute.disputeInfo.tags.map((t, i) => (
                          <span key={i} className="tag tag-sm tag-active">{t}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* History */}
              <div className="edit-card">
                <div className="edit-card-header">تاریخچه اقدامات</div>
                <div className="edit-card-body">
                  {selectedDispute.disputeInfo.actionHistory && selectedDispute.disputeInfo.actionHistory.length > 0 ? (
                    <div style={{ maxHeight: 260, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {selectedDispute.disputeInfo.actionHistory.map((action, idx) => (
                        <div
                          key={idx}
                          style={{
                            padding: 10,
                            background: 'var(--color-bg-paper)',
                            border: '1px solid var(--color-border)',
                            borderRadius: 8,
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <strong style={{ fontSize: 13 }}>
                              {action.action === 'refund_item' ? 'بازپرداخت'
                                : action.action === 'approve' ? 'تأیید'
                                  : action.action === 'reject' ? 'رد'
                                    : action.action === 'resolve' ? 'حل شده'
                                      : action.action === 'penalty' ? 'جریمه'
                                        : action.action === 'restriction' ? 'محدودیت'
                                          : action.action}
                            </strong>
                            {action.performedAt && (
                              <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                                {formatDate(action.performedAt)}
                              </span>
                            )}
                          </div>
                          {action.performedByName && (
                            <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 4 }}>
                              توسط: {action.performedByName}
                            </div>
                          )}
                          {action.details?.reason && (
                            <div style={{ fontSize: 12, marginTop: 4 }}>دلیل: {action.details.reason}</div>
                          )}
                          {action.details?.refundAmount && (
                            <div style={{ fontSize: 12, marginTop: 4, color: '#16a34a' }}>
                              مبلغ: {formatPrice(action.details.refundAmount)} تومان
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>هنوز اقدامی ثبت نشده است</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={() => setShowDetailsDialog(false)}>بستن</button>
        </div>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog
        open={showRejectDialog}
        onClose={() => setShowRejectDialog(false)}
        maxWidth="sm"
        fullWidth
        className="dispute-action-dialog"
      >
        <div className="edit-dialog-title">
          <span>رد اعتراض</span>
          <button className="details-close" onClick={() => setShowRejectDialog(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          <div className="edit-form">
            <div className="edit-card">
              <div className="edit-card-header">دلیل رد</div>
              <div className="edit-card-body">
                <div className="edit-field full">
                  <label>دلیل رد اعتراض را وارد کنید</label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="دلیل رد اعتراض..."
                    rows={4}
                  />
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={() => setShowRejectDialog(false)}>انصراف</button>
          <button
            className="edit-btn submit"
            style={{ background: '#dc2626' }}
            onClick={handleRejectDispute}
            disabled={!rejectReason.trim() || actionLoading === selectedDispute?._id}
          >
            {actionLoading === selectedDispute?._id ? <CircularProgress size={16} style={{ color: '#fff' }} /> : <CancelIcon fontSize="small" />}
            رد اعتراض
          </button>
        </div>
      </Dialog>

      {/* Resolve Dialog */}
      <Dialog
        open={showResolveDialog}
        onClose={() => setShowResolveDialog(false)}
        maxWidth="sm"
        fullWidth
        className="dispute-action-dialog"
      >
        <div className="edit-dialog-title">
          <span>حل اعتراض</span>
          <button className="details-close" onClick={() => setShowResolveDialog(false)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          <div className="edit-form">
            <div className="edit-card">
              <div className="edit-card-header">نتیجه حل اعتراض</div>
              <div className="edit-card-body">
                <div className="edit-field full">
                  <label>نتیجه را شرح دهید</label>
                  <textarea
                    value={resolveReason}
                    onChange={(e) => setResolveReason(e.target.value)}
                    placeholder="نتیجه حل اعتراض..."
                    rows={4}
                  />
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <div className="edit-dialog-actions">
          <button className="edit-btn cancel" onClick={() => setShowResolveDialog(false)}>انصراف</button>
          <button
            className="edit-btn submit"
            style={{ background: '#16a34a' }}
            onClick={handleResolveDispute}
            disabled={!resolveReason.trim() || actionLoading === selectedDispute?._id}
          >
            {actionLoading === selectedDispute?._id ? <CircularProgress size={16} style={{ color: '#fff' }} /> : <CheckCircleIcon fontSize="small" />}
            حل اعتراض
          </button>
        </div>
      </Dialog>

      {/* Refund Dialog */}
      {selectedDispute && disputeType === 'item' && (() => {
        const itemDispute = selectedDispute as OrderItemDispute;
        const isPostal = itemDispute.fulfillmentType === 'postal_delivery';
        const shippingCost = isPostal
          ? (itemDispute.postalDeliveryDetails?.shippingCost || 0) * (itemDispute.quantity || 1)
          : 0;
        const productPrice = itemDispute.totalPrice || 0;
        const totalRefund = productPrice + (includeShipping && isPostal ? shippingCost : 0);

        return (
          <Dialog
            open={showRefundDialog}
            onClose={() => setShowRefundDialog(false)}
            maxWidth="sm"
            fullWidth
            className="dispute-action-dialog"
          >
            <div className="edit-dialog-title">
              <span>بازپرداخت آیتم سفارش</span>
              <button className="details-close" onClick={() => setShowRefundDialog(false)}>
                <CloseIcon fontSize="small" />
              </button>
            </div>
            <DialogContent className="edit-dialog-content">
              <div className="edit-form">
                <div className="edit-card">
                  <div className="edit-card-header">اطلاعات محصول</div>
                  <div className="edit-card-body">
                    <div className="edit-form-grid">
                      <div className="edit-field">
                        <label>محصول</label>
                        <input type="text" readOnly value={itemDispute.productTitle} />
                      </div>
                      <div className="edit-field">
                        <label>فروشگاه</label>
                        <input type="text" readOnly value={itemDispute.storeId?.storeName || '—'} />
                      </div>
                      <div className="edit-field">
                        <label>تعداد</label>
                        <input type="text" readOnly value={String(itemDispute.quantity)} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="edit-card">
                  <div className="edit-card-header">محاسبه مبلغ بازپرداخت</div>
                  <div className="edit-card-body">
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                      <span style={{ fontSize: 13 }}>قیمت محصول:</span>
                      <strong style={{ fontSize: 13 }}>{formatPrice(productPrice)} تومان</strong>
                    </div>
                    {isPostal && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', alignItems: 'center' }}>
                        <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input
                            type="checkbox"
                            checked={includeShipping}
                            onChange={(e) => setIncludeShipping(e.target.checked)}
                          />
                          هزینه ارسال پستی
                        </label>
                        <strong
                          style={{
                            fontSize: 13,
                            textDecoration: includeShipping ? 'none' : 'line-through',
                            color: includeShipping ? 'inherit' : 'var(--color-text-disabled)',
                          }}
                        >
                          {formatPrice(shippingCost)} تومان
                        </strong>
                      </div>
                    )}
                    <div style={{ height: 1, background: 'var(--color-border)', margin: '8px 0' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <strong style={{ fontSize: 14, color: '#ea580c' }}>مبلغ کل بازپرداخت:</strong>
                      <strong style={{ fontSize: 14, color: '#ea580c' }}>{formatPrice(totalRefund)} تومان</strong>
                    </div>
                    <Alert severity="info" sx={{ mt: 2, fontSize: '0.75rem' }}>
                      مالیات برگشت داده نمی‌شود و متعلق به سایت است
                    </Alert>
                  </div>
                </div>

                <div className="edit-card">
                  <div className="edit-card-header">دلیل بازپرداخت</div>
                  <div className="edit-card-body">
                    <div className="edit-field full">
                      <label>دلیل (اختیاری)</label>
                      <textarea
                        value={refundReason}
                        onChange={(e) => setRefundReason(e.target.value)}
                        placeholder="دلیل بازپرداخت..."
                        rows={3}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </DialogContent>
            <div className="edit-dialog-actions">
              <button className="edit-btn cancel" onClick={() => setShowRefundDialog(false)}>انصراف</button>
              <button
                className="edit-btn submit"
                style={{ background: '#ea580c' }}
                onClick={handleRefundItem}
                disabled={actionLoading === selectedDispute._id}
              >
                {actionLoading === selectedDispute._id ? <CircularProgress size={16} style={{ color: '#fff' }} /> : <MonetizationOnIcon fontSize="small" />}
                بازپرداخت {formatPrice(totalRefund)} تومان
              </button>
            </div>
          </Dialog>
        );
      })()}

      {/* Unified Resolution Dialog */}
      <UnifiedResolutionDialog
        open={unifiedDialogOpen}
        onClose={() => setUnifiedDialogOpen(false)}
        dispute={selectedUnifiedDispute}
        onAction={handleUnifiedAction}
        loading={actionLoading !== null}
        error={unifiedDialogError}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default DisputeManagement;
